import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import moment from "moment";

import { ApiCallResult, createDefaultApiCallResult, useAPI } from "services/Api";
import CompletedCurrentWeekView from "planEditor/CompletedCurrentWeekView";
import CompletedFutureWeekView from "planEditor/CompletedFutureWeekView";
import CompletedPastWeekView from "planEditor/CompletedPastWeekView";
import CurrentWeekView from "planEditor/CurrentWeekView";
import DefaultZeroView from "planEditor/DefaultZeroView";
import FutureWeekView from "planEditor/FutureWeekView";
import NewPlan from "planEditor/NewPlan";
import PastWeekView from "planEditor/PastWeekView";
import PatientProfile from "pages/PatientProfile";
import { delValueAtPath, getValueAtPath, setValueAtPath } from "utility/ObjectUtils";
import { renderLoader } from "utility/LoaderUtils";
import { useNotificationManager } from "notifications/NotificationManager";
import { useSessionState } from "../utility/SessionManager";

export const EditCoachingPlan = ({ componentName = "[EditCoachingPlan]" }) => {
    const [activities, setActivities] = useState(null);
    const [activityStats, setActivityStats] = useState(null);
    const [bestPriority, setBestPriority] = useState(null);
    const [coachingPlanTrackOptions, setCoachingPlanTrackOptions] = useState(null);
    const [coachingPlans, setCoachingPlans] = useState(null);
    const [constPlan, setConstPlan] = useState(null);
    const [constRoutine, setConstRoutine] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(null);
    const [duplicatePlans, setDuplicatePlans] = useState(null);
    const [duplicateProtocol, setDuplicateProtocol] = useState(null);
    const [editPlanSettings, setEditPlanSettings] = useState(false);
    const [havePlan, setPlan] = useState(false);
    const [init, setInit] = useState(false);
    const [instanceID, setInstanceId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState("Loading Coaching Plan");
    const [patientName, setPatientName] = useState(null);
    const [personalRoutine, setPersonalRoutine] = useState(null);
    const [personalRoutineEnabled, setPersonalRoutineEnabled] = useState(false);
    const [planName, setPlanName] = useState("_");
    const [revertedPlans, setRevertedPlans] = useState(null);
    const [revertedRoutine, setRevertedRoutine] = useState(null);
    const [selectedCoachingPlanTrack, setSelectedCoachingPlanTrack] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [settings, setSettings] = useState(null);
    const [startDate, _setStartDate] = useState(new Date());
    const [stateView, setStateView] = useState(null);
    const [timeZone, setTimeZone] = useState(null);
    const [trackInstance, setTrackInstance] = useState(null);
    const [trackPlan, setTrackPlans] = useState(null);
    const [vetKitVersion, setVetKitVersion] = useState(null);
    const [weekOptions, setWeekOptions] = useState(null);
    const [restrictCoachingPlanEnd, setRestrictCoachingPlanEnd] = useState(false);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const patientId = (useParams() as { id: string }).id;
    const { canMakeApiCall } = useSessionState();

    /////////////////////////////////////
    //  ASYNC COACHING PLAN FUNCTIONS  //
    /////////////////////////////////////

    const redoState = () => {
        setStateView(renderState());
        void getCurrentPlan(activities, activityStats);
    };

    const parseTrackName = (name) => {
        return name.replace(/_|-|\\. /g, " ");
    };

    const savePlan = async () => {
        if (!canMakeApiCall()) {
            console.warn(`${componentName}[savePlan]: Skipping API calls. Session not active`);
            return;
        }

        notificationManager.clearAllNotifications();
        setLoadingMessage("Saving Coaching Plan");
        setIsLoading(true);

        let planSaved: boolean = false;
        let plansToSave = coachingPlans;
        setCoachingPlans(null);

        let result: ApiCallResult = createDefaultApiCallResult({ wasSuccessful: true });

        let newId = null;
        for (let i = 0; i < plansToSave.length; i++) {
            if (i >= currentWeek) {
                let cleanedAssignments = cleanAssignments(plansToSave[i].assignments);
                if (duplicatePlans[i].assignments !== JSON.stringify(cleanedAssignments)) {
                    let data = {
                        patient: patientId,
                        time_zone: plansToSave[i].time_zone,
                        start_date: moment(plansToSave[i].start_date).format("YYYY-MM-DD"),
                        track_name: plansToSave[i].track_name,
                        plan_title: plansToSave[i].plan_title,
                        plan_week: plansToSave[i].plan_week,
                        assignments: cleanedAssignments,
                        state: plansToSave[i].state
                    };
                    if (currentWeek === i) {
                        result = await API
                            .api_Post(
                                componentName,
                                "/coaching_plans/",
                                JSON.stringify(data),
                                notificationManager,
                                (response) => {
                                    newId = response.data.id;
                                    planSaved = true;
                                },
                                (error) => {
                                    notificationManager.addError(error);
                                }
                            );
                    }
                    else {
                        result = await API
                            .api_Put(
                                componentName,
                                `/coaching_plans/${plansToSave[i].id}/`,
                                JSON.stringify(data),
                                notificationManager,
                                (_response) => {
                                    planSaved = true;
                                },
                                (error) => {
                                    notificationManager.addError(error);
                                }
                            );
                    }
                }

                if (!result.wasSuccessful) {
                    break;
                }
            }
        }

        if (result.wasSuccessful) {
            let cleanedProtocolAssignments = cleanAssignments(personalRoutine.assignments);
            if (personalRoutine.enabled !== personalRoutineEnabled || duplicateProtocol !== JSON.stringify(cleanedProtocolAssignments)) {
                result = await API.api_Put(
                    componentName,
                    `/personal_routines/${personalRoutine.id}/`,
                    {
                        patient: patientId,
                        assignments: cleanedProtocolAssignments,
                        enabled: personalRoutine.enabled
                    },
                    notificationManager,
                    (_response) => {
                        planSaved = true;
                    },
                    (error) => {
                        notificationManager.addError(error);
                    }
                );
            }
        }

        if (result.wasSuccessful) {
            if (newId !== null) {
                result = await API.api_Post(
                    componentName,
                    `coaching_plans/${newId}/replace/`,
                    null,
                    notificationManager,
                    (_response) => {
                        planSaved = true;
                    },
                    (error) => {
                        notificationManager.addError(error);
                        setIsLoading(false);
                    }
                );
            }
        }

        if (result.wasSuccessful && planSaved) {
            notificationManager.addSuccess("Saved Coaching Plan.", { duration: 10 });
            redoState();
        }
    };

    const updatePlanSettings = async () => {
        if (!canMakeApiCall()) {
            console.warn(`${componentName}[updatePlanSettings]: Skipping API calls. Session not active`);
            return;
        }

        let trackName = parseTrackName(selectedCoachingPlanTrack);

        notificationManager.clearAllNotifications();
        setIsLoading(true);
        setLoadingMessage(`Changing Coaching Plan to ${trackName}`);

        let temp = coachingPlans;
        setCoachingPlans(null);

        let result: ApiCallResult = createDefaultApiCallResult({ wasSuccessful: true });

        if (temp[0].track_name !== "Default Week Zero") {
            for (let i = 0; i < temp.length; i++) {
                let data = {
                    patient: patientId,
                    time_zone: timeZone,
                    start_date: moment(temp[i].start_date).format("YYYY-MM-DD"),
                    track_name: trackName,
                    plan_title: (i <= 12 ? trackPlan[selectedCoachingPlanTrack].plans[i].title : ""),
                    plan_week: temp[i].plan_week,
                    assignments: cleanAssignments(i <= 12 ? (formatPlanAssignments(trackPlan[selectedCoachingPlanTrack].plans[i].assignments)) : (formatPlanAssignments(trackPlan[selectedCoachingPlanTrack].plans[12].assignments))),
                    state: temp[i].state
                };

                if (currentWeek === i) {
                    let newId: number = null;
                    result = await API.api_Post(
                        componentName,
                        "/coaching_plans/",
                        JSON.stringify(data),
                        notificationManager,
                        (response) => {
                            newId = response.data.id;
                        },
                        (error) => {
                            notificationManager.addError(error);
                        }
                    );

                    if (result.wasSuccessful) {
                        if (newId !== null) {
                            result = await API.api_Post(
                                componentName,
                                `/coaching_plans/${newId}/replace/`,
                                null,
                                notificationManager,
                                (_response) => {
                                },
                                (error) => {
                                    notificationManager.addError(error);
                                }
                            );
                        }
                    }

                    if (!result.wasSuccessful) {
                        break;
                    }
                }
                else if (i > currentWeek) {
                    result = await API.api_Put(
                        componentName,
                        `coaching_plans/${temp[i].id}/`,
                        JSON.stringify(data),
                        notificationManager,
                        (_response) => {
                        },
                        (error) => {
                            notificationManager.addError(error);
                        }
                    );

                    if (!result.wasSuccessful) {
                        break;
                    }
                }
            }
        }
        else {
            for (let i = 0; i < (temp.length > 13 ? temp.length : 13); i++) {
                if (currentWeek === i) {
                    let data = {
                        patient: patientId,
                        time_zone: timeZone,
                        start_date: moment(temp[i].start_date).format("YYYY-MM-DD"),
                        track_name: trackName,
                        plan_title: trackPlan[selectedCoachingPlanTrack].plans[0].title,
                        plan_week: temp[i].plan_week,
                        assignments: cleanAssignments(temp[i].assignments),
                        state: temp[i].state
                    };

                    let newId: number = null;
                    result = await API.api_Post(
                        componentName,
                        "/coaching_plans/",
                        JSON.stringify(data),
                        notificationManager,
                        (response) => {
                            newId = response.data.id;
                        },
                        (error) => {
                            notificationManager.addError(error);
                        }
                    );

                    if (result.wasSuccessful) {
                        if (newId !== null) {
                            result = await API.api_Post(
                                componentName,
                                `/coaching_plans/${newId}/replace/`,
                                null,
                                notificationManager,
                                (_response) => {
                                },
                                (error) => {
                                    notificationManager.addError(error);
                                }
                            );
                        }
                    }

                    if (!result.wasSuccessful) {
                        break;
                    }
                }
                else if (i > currentWeek) {
                    let data = {
                        patient: patientId,
                        time_zone: timeZone,
                        start_date: moment(temp[0].start_date).add(7 * i, "days").format("YYYY-MM-DD"),
                        track_name: trackName,
                        plan_title: trackPlan[selectedCoachingPlanTrack].plans[0].title,
                        plan_week: i,
                        assignments: cleanAssignments(temp[i].assignments),
                        state: "PENDING"
                    };
                    result = await API.api_Post(
                        componentName,
                        "/coaching_plans/",
                        JSON.stringify(data),
                        notificationManager,
                        (_response) => {
                        },
                        (error) => {
                            notificationManager.addError(error);
                        }
                    );

                    if (!result.wasSuccessful) {
                        break;
                    }
                }
            }
        }

        if (result.wasSuccessful) {
            if (personalRoutine !== null) {
                let priorityprot = formatPlanAssignments(trackPlan[selectedCoachingPlanTrack].default_personal_routine.assignments);
                let data = { patient: patientId, assignments: cleanAssignments(priorityprot), enabled: personalRoutine.enabled };

                result = await API.api_Put(
                    componentName,
                    `/personal_routines/${personalRoutine.id}/`,
                    JSON.stringify(data),
                    notificationManager,
                    (_response) => {
                    },
                    (error) => {
                        notificationManager.addError(error);
                    }
                );
            }
            else {
                let priorityprot = formatPlanAssignments(trackPlan[selectedCoachingPlanTrack].default_personal_routine.assignments);
                let data = { patient: patientId, assignments: cleanAssignments(priorityprot) };

                result = await API.api_Put(
                    componentName,
                    "/personal_routines/",
                    JSON.stringify(data),
                    notificationManager,
                    (_response) => {
                    },
                    (error) => {
                        notificationManager.addError(error);
                    }
                );
            }
        }

        if (result.wasSuccessful) {
            if (instanceID !== null) {
                let trackName = parseTrackName(selectedCoachingPlanTrack);
                result = await API.api_Put(
                    componentName,
                    `/coaching_plan_track_instances/${instanceID}/`,
                    JSON.stringify({ track_name: trackName }),
                    notificationManager,
                    (_response) => {
                    },
                    (error) => {
                        notificationManager.addError(error);
                    }
                );
            }
        }

        if (result.wasSuccessful) {
            notificationManager.addSuccess(`Changed Coaching Plan to ${trackName}.`, { duration: 10 });
            void loadEditor();
        }
        else {
            notificationManager.addError(`Failed to change Coaching Plan to ${trackName}.`);
            setIsLoading(false);
        }
    };

    const formatPlanAssignments = (arr) => {
        let result = [];
        arr.forEach(element => {
            if (element !== null && Object.hasOwn(element,"Id")) {
                result.push({ "Id": element.Id });
            }
            else {
                result.push({ "Id": element });
            }
        });

        return result;
    };

    const createNewPlan = async () => {
        if (!canMakeApiCall()) {
            console.warn(`${componentName}[createNewPlan]: Skipping API calls. Session not active`);
            return;
        }

        let trackName = parseTrackName(selectedCoachingPlanTrack);
        setPlanName(trackName);

        notificationManager.clearAllNotifications();
        setIsLoading(true);
        setLoadingMessage(`Creating new ${trackName} Coaching Plan`);

        let result: ApiCallResult = createDefaultApiCallResult({ wasSuccessful: true });

        let plans = trackPlan[selectedCoachingPlanTrack].plans;
        let personalRoutineAssignments = cleanAssignments(formatPlanAssignments(trackPlan[selectedCoachingPlanTrack].default_personal_routine.assignments));

        let week0Id = null;
        for (let i = 0; i < plans.length; i++) {
            let data = {
                patient: patientId,
                time_zone: timeZone,
                start_date: moment(startDate).add(7 * i, "days").format("YYYY-MM-DD"),
                track_name: trackPlan[selectedCoachingPlanTrack].track_name,
                plan_title: trackPlan[selectedCoachingPlanTrack].plans[i].title,
                plan_week: i,
                assignments: cleanAssignments(formatPlanAssignments(plans[i].assignments)),
                state: "PENDING"
            };
            result = await API.api_Post(
                componentName,
                "/coaching_plans/",
                JSON.stringify(data),
                notificationManager,
                (response) => {
                    if (i === 0) {
                        week0Id = response.data.id;
                    }
                },
                (error) => {
                    notificationManager.addError(error);
                }
            );

            if (!result.wasSuccessful) {
                break;
            }
        }

        if (result.wasSuccessful) {
            if (week0Id !== null) {
                result = await API.api_Post(
                    componentName,
                    `coaching_plans/${week0Id}/activate/`,
                    null,
                    notificationManager,
                    (_response) => {
                    },
                    (error) => {
                        notificationManager.addError([`Error starting week ${week0Id}`, error]);
                    }
                );
            }
        }

        if (result.wasSuccessful) {
            result = await API.api_Post(
                componentName,
                "/personal_routines/",
                JSON.stringify({ patient: patientId, assignments: personalRoutineAssignments }),
                notificationManager,
                (_response) => {
                },
                (error) => {
                    notificationManager.addError(error);
                }
            );
        }

        if (result.wasSuccessful) {
            notificationManager.addSuccess(`Created new ${trackName} Coaching Plan.`, { duration: 10 });
            void loadEditor();
        }
        else {
            notificationManager.addError(`Failed to create new ${trackName} Coaching Plan.`);
            setIsLoading(false);
        }
    };

    const endPlan = async () => {
        if (haveAssignmentsChanged()) {
            let text = "\nWARNING!\n\nThere are unsaved changes to the Coaching Plan!\n\nEnding the plan will result in the loss of these changes.\n\nAre you sure you want to end the plan?\n";
            if (!window.confirm(text)) {
                return;
            }
        }
        else {
            let text = "\nWARNING!\n\nEnding the plan will prevent any further progress or edits. If the patient needs to continue with the Karuna program, a new plan will need to be created or admin assistance will be required to revive this plan.\n\nAre you sure you want to end the plan?\n";
            if (!window.confirm(text)) {
                return;
            }
        }

        if (!canMakeApiCall()) {
            console.warn(`${componentName}[endPlan]: Skipping API calls. Session not active`);
            return;
        }

        notificationManager.clearAllNotifications();
        setIsLoading(true);
        setLoadingMessage("Ending Coaching Plan");

        let result: ApiCallResult = createDefaultApiCallResult({ wasSuccessful: true });

        let week = currentWeek + 1;
        if (week >= coachingPlans.length) {
            result = await API.api_Post(
                componentName,
                `coaching_plans/${coachingPlans[coachingPlans.length - 1].id}/complete/`,
                null,
                notificationManager,
                (_response) => {
                },
                (error) => {
                    notificationManager.addError([`Errors ending Coaching Plan at week ${week}`, error]);
                }
            );
        }

        if (result.wasSuccessful) {
            result = await API.api_Post(
                componentName,
                `coaching_plan_track_instances/${trackInstance.id}/complete/`,
                null,
                notificationManager,
                (_response) => {
                },
                (error) => {
                    notificationManager.addError(["Errors ending Coaching Plan", error]);
                }
            );
        }

        if (result.wasSuccessful) {
            notificationManager.addSuccess("Coaching Plan ended.", { duration: 10 });
            void loadEditor();
        }
        else {
            notificationManager.addError("Failed to end Coaching Plan.");
            setIsLoading(false);
        }
    };

    const startWeek = async () => {
        if (hasPlanChanged()) {
            let text = "\nWARNING!\n\nThere are unsaved changes to the Coaching Plan!\n\nEnding the plan will result in the loss of these changes.\n\nAre you sure you want to complete this week?\n";
            if (!window.confirm(text)) {
                return;
            }
        }

        if (!canMakeApiCall()) {
            console.warn(`${componentName}[endPlan]: Skipping API calls. Session not active`);
            return;
        }

        notificationManager.clearAllNotifications();

        let newWeek = currentWeek + 1;
        if (newWeek >= coachingPlans.length) {
            notificationManager.addError(`Unable to start Week ${newWeek}. The Coaching Plan only has ${coachingPlans.length} week${coachingPlans.length === 1 ? "" : "s"}.`);
            return;
        }

        setIsLoading(true);
        setLoadingMessage(`Starting Coaching Plan Week ${newWeek}`);

        let result: ApiCallResult = createDefaultApiCallResult({ wasSuccessful: true });

        let planCompleted: boolean = false;
        result = await API.api_Post(
            componentName,
            `coaching_plans/${coachingPlans[currentWeek].id}/complete/`,
            null,
            notificationManager,
            (_response) => {
                planCompleted = true;
            },
            (error) => {
                notificationManager.addError([`Error completing Coaching Plan Week ${currentWeek}`, error]);
                setIsLoading(false);
            }
        );

        if (result.wasSuccessful && planCompleted) {
            result = await API.api_Post(
                componentName,
                `coaching_plans/${coachingPlans[newWeek].id}/activate/`,
                null,
                notificationManager,
                (_response) => {
                    setCurrentWeek(newWeek);
                },
                (error) =>{
                    notificationManager.addError([`Error starting Coaching Plan Week ${newWeek}`, error]);
                }
            );
        }

        if (result.wasSuccessful) {
            notificationManager.addSuccess(`Week ${newWeek} started`, { duration: 10 });
            void loadEditor();
        }
        else {
            notificationManager.addError(`Failed to start Week ${newWeek}.`);
            setIsLoading(false);
        }
    };

    // ---=== KarunaCLOUD ===--- //

    const getProtocols = async (): Promise<any> => {
        const result: ApiCallResult = await API.api_Get(componentName, "/protocols", notificationManager);

        if (!result.wasSuccessful) {
            return null;
        }

        let protocols = result.response?.data?.results;
        if (!protocols || !protocols.length) {
            return null;
        }

        protocols = protocols.filter(protocol => (protocol.activities.length === 1 && !protocol.id.includes("Pro") && (!actFilter.includes(protocol.id))));
        setActivities(protocols);
        return protocols;
    };

    const getActivityStats = async (): Promise<any> => {
        const result: ApiCallResult = await API.api_Get(componentName, `/activity_stats/?patient_id=${patientId}`, notificationManager);

        if (!result.wasSuccessful) {
            return null;
        }

        let activityStats = result.response?.data?.activity_stats;

        if (!activityStats) {
            return null;
        }

        setSettings(activityStats);

        let actStats = {};
        for (const [key, value] of Object.entries(activityStats) as [string, any]) {
            actStats[key] = {};
            actStats[key]["name"] = value.name;
            actStats[key]["type"] = value.type;
            actStats[key]["min_version"] = value.min_version;
            actStats[key]["Settings"] = value.Settings;
            actStats[key]["assigned"] = value.assigned;
            actStats[key]["completions"] = value.completions;
            if (value.weekly_stats !== undefined) {
                actStats[key]["weekly_stats"] = value.weekly_stats;
            }
            actStats[key]["hasOptions"] = value.Settings && Object.entries(value.Settings).length > 2;
            if (actStats[key]["hasOptions"]) {
                actStats[key]["optionLabels"] = [];
                actStats[key]["optionDescriptions"] = [];
                Object.entries(value.Settings).forEach(([name, value]: [string, any]) => {
                    if (name !== "Duration" && name !== "DurationIncrement") {
                        actStats[key]["optionDescriptions"].push(value.Description);
                        actStats[key]["optionLabels"].push(name);
                    }
                });
            }
        }
        setActivityStats(actStats);

        return actStats;
    };

    const getCoachingPlanTrackData = async () => {
        const result: ApiCallResult = await API.api_Get(componentName, "/coaching_plan_tracks", notificationManager);

        if (!result.wasSuccessful) {
            return null;
        }

        let coachingPlanTracks = result.response?.data?.results;

        if (!coachingPlanTracks || !coachingPlanTracks.length) {
            return null;
        }

        let tracks = {};
        let options = [];
        coachingPlanTracks.forEach((element, i) => {
            tracks[element.id] = element;
            options.push({ label: element.track_name, value: element.id });
        });
        setCoachingPlanTrackOptions(options);
        setTrackPlans(tracks);
    };

    const getCoachingPlanTrackInstances = async (): Promise<any> => {
        const result: ApiCallResult = await API.api_Get(componentName, `/coaching_plan_track_instances/?patient=${patientId}`, notificationManager);

        if (!result.wasSuccessful) {
            return null;
        }

        let coachingPlanTracksInstances = result.response?.data?.results;
        if (!coachingPlanTracksInstances || !coachingPlanTracksInstances.length) {
            return null;
        }

        const sortedTracks = coachingPlanTracksInstances.sort((a, b) =>
            Number.parseInt(a.id) > Number.parseInt(b.id) ? -1 : 1
        );

        let currentTrack = sortedTracks[0];
        setTrackInstance(currentTrack);
        setInstanceId(currentTrack ? currentTrack.id : null);

        return currentTrack;
    };

    const getCoachingPlans = async (currentTrack, acts, aStats) => {
        if (currentTrack === null || acts === null) {
            return null;
        }

        // Fetch all pages of coaching plans to handle patients with many weeks
        let allPlans = [];
        let nextUrl = `/coaching_plans/?patient=${patientId}&track_instance=${currentTrack.id}&page_size=1000`;

        while (nextUrl) {
            const result = await API.api_Get(componentName, nextUrl, notificationManager);
            if (!result.wasSuccessful) {
                return null;
            }

            const pageResults = result.response?.data?.results || [];
            allPlans = allPlans.concat(pageResults);

            // Check for next page
            nextUrl = result.response?.data?.next;
        }

        if (!allPlans.length) {
            return null;
        }

        let plansById = allPlans;

        // Use a Map to handle duplicate week numbers properly
        let plansByWeekMap = new Map();

        // Sort by ID descending so we process newer plans first, then by state (Active first)
        plansById = plansById.sort((a, b) => {
            // Active plans should come before non-Active plans
            if (a.state === "Active" && b.state !== "Active") return -1;
            if (a.state !== "Active" && b.state === "Active") return 1;
            // Then sort by ID descending (newer first)
            return Number.parseInt(b.id) - Number.parseInt(a.id);
        });

        // console.log(`${componentName}[getCoachingPlans]: Processing ${plansById.length} plans`);
        // console.log(`${componentName}[getCoachingPlans]: Raw week numbers from API:`, plansById.map(p => `${p.id}:W${p.plan_week}(${p.state})`).join(", "));

        plansById.forEach(
            element => {
                const weekNum = element.plan_week;
                const existingPlan = plansByWeekMap.get(weekNum);

                // If we already have a plan for this week
                if (existingPlan) {
                    // Keep the existing plan if it's Active, otherwise update
                    if (existingPlan.state === "Active") {
                        return;
                    }
                }

                // Skip Replaced plans if we already have a better one
                if (element.state === "Replaced" && existingPlan) {
                    return;
                }

                // Don't add Replaced plans at all unless there's no alternative
                if (element.state === "Replaced") {
                    return;
                }

                let ass = element.assignments;
                plansByWeekMap.set(weekNum, {
                    id: element.id,
                    time_zone: element.time_zone,
                    start_date: moment(element.start_date),
                    track_name: element.track_name,
                    plan_title: element.plan_title,
                    plan_week: element.plan_week,
                    assignments: convertOldAssignmentDurations(ass),
                    state: element.state
                });
            }
        );

        // Convert Map to array and sort by week number
        let plans = Array.from(plansByWeekMap.values());
        // console.log(`${componentName}[getCoachingPlans]: Filtered to ${plans.length} plans. Week numbers: ${plans.map(p => p.plan_week).join(", ")}`);

        if (plans.length > 0) {
            let plansByWeek = plans.sort((a, b) => (Number.parseInt(a.plan_week) > Number.parseInt(b.plan_week)) ? 1 : -1);
            let formattedPlans = formatPlans(plansByWeek.map(arrayElement => Object.assign({}, arrayElement)), acts);

            const deepCopyDuplicatePlans = JSON.parse(JSON.stringify(formattedPlans));
            deepCopyDuplicatePlans.forEach(
                (plan, _index) => {
                    plan["assignments"] = JSON.stringify(cleanAssignments(plan["assignments"], aStats));
                }
            );

            setCoachingPlans(formattedPlans);
            setConstPlan(JSON.parse(JSON.stringify(formattedPlans)));
            setRevertedPlans(JSON.stringify(formattedPlans));
            setDuplicatePlans(deepCopyDuplicatePlans);

            if (plansByWeek[0].track_name !== "Default Week Zero") {
                setPlan(true);
            }
            else {
                setEditPlanSettings(true);
            }

            setWeek(plansByWeek);
            configureWeekOptions(plansByWeek);
        }
    };

    const getPersonalRoutine = async (currentTrack: any, aStats = activityStats) => {
        if (currentTrack === null) {
            return null;
        }

        const result: ApiCallResult = await API.api_Get(componentName, `/personal_routines/?patient=${patientId}`, notificationManager);

        if (!result.wasSuccessful) {
            return null;
        }

        let personRoutines = result.response?.data?.results;

        if (!personRoutines?.length) {
            return null;
        }

        personRoutines = personRoutines.sort((a, b) => (Number.parseInt(a.id) > Number.parseInt(b.id)) ? 1 : -1);

        const index = personRoutines.length - 1;
        const currentRoutine = personRoutines[index];

        const mostRecentPersonalRoutine = {
            id: currentRoutine.id,
            patient: currentRoutine.patient,
            assignments: convertOldAssignmentDurations(currentRoutine.assignments),
            state: currentRoutine.state,
            enabled: currentRoutine.enabled
        };

        setPersonalRoutine(mostRecentPersonalRoutine);
        setConstRoutine(JSON.parse(JSON.stringify(mostRecentPersonalRoutine)));
        setRevertedRoutine(JSON.stringify(currentRoutine.assignments));
        setPersonalRoutineEnabled(mostRecentPersonalRoutine.enabled);

        const personalRoutineErrors = [];
        const duplicateError = validatePersonalRoutineAssignmentsUnique(mostRecentPersonalRoutine.assignments);

        if (duplicateError) {
            personalRoutineErrors.push(`Error loading Coaching Plan:\n${duplicateError}Contact a Karuna Admin for support.`);
        }

        if (mostRecentPersonalRoutine.assignments.length !== 7 && currentTrack.track_name !== "Default Week Zero") {
            personalRoutineErrors.push(
                `Error loading Coaching Plan:\nThe routine has an irregular number (${mostRecentPersonalRoutine.assignments.length}) of activities assigned to it.\nContact a Karuna Admin for support.`
            );
        }

        if (personalRoutineErrors.length) {
            notificationManager.addError(personalRoutineErrors);
        }

        const cleanedProtocolAssignments = JSON.stringify(cleanAssignments(currentRoutine.assignments, aStats));
        setDuplicateProtocol(cleanedProtocolAssignments);
    };

    const getPersonalRoutineProgress = async (currentTrack: any) => {
        if (currentTrack === null) {
            return null;
        }

        const result: ApiCallResult = await API.api_Get(componentName, `/personal_routine_progress/?patient=${patientId}`, notificationManager);

        if (!result.wasSuccessful) {
            return null;
        }

        let personRoutineProgressRecords = result.response?.data?.results;

        if (!personRoutineProgressRecords?.length) {
            return null;
        }

        let ratingDict = {};
        personRoutineProgressRecords.forEach(
            element => {
                if (element.track_instance === currentTrack.id && element.is_complete) {
                    if (element.plan_week in ratingDict) {
                        if (element.rating !== undefined && element.rating.score !== undefined) {
                            let tempCount = ratingDict[element.plan_week].count;
                            if (element.rating.score > ratingDict[element.plan_week].rating.score) {
                                ratingDict[element.plan_week] = element;
                            }
                            element.count = tempCount + 1;
                        }
                        else {
                            if (ratingDict[element.plan_week].rating === undefined) {
                                if (ratingDict[element.plan_week].scores.length <= element.scores.length) {
                                    element.count = 0;
                                    element.rating = {};
                                    element.rating.score = 0;
                                    ratingDict[element.plan_week] = element;
                                }
                            }
                            ratingDict[element.plan_week].count++;
                        }
                    }
                    else {
                        if (element.rating !== undefined && element.rating.score !== undefined) {
                            element.count = 1;
                            ratingDict[element.plan_week] = element;
                        }
                        else {
                            element.count = 0;
                            element.rating = {};
                            element.rating.score = 0;
                            ratingDict[element.plan_week] = element;
                        }
                    }
                }
            }
        );

        if (bestPriority === null || Object.keys(bestPriority).length === 0 || Object.keys(bestPriority).length < Object.keys(ratingDict).length) {
            setBestPriority(ratingDict);
        }
    };

    const getCurrentPlan = async (acts = activities, aStats = activityStats) => {

        setIsLoading(true);
        setLoadingMessage("Loading Coaching Plan");

        await getCoachingPlanTrackInstances()
            .then(
                (currentTrack) => {
                    Promise
                        .all([
                            getCoachingPlans(currentTrack, acts, aStats),
                            getPersonalRoutine(currentTrack, aStats),
                            getPersonalRoutineProgress(currentTrack)
                        ])
                        .catch(
                            (error) => {
                                notificationManager.addError(error);
                            }
                        )
                        .finally(
                            () => {
                                setIsLoading(false);
                            }
                        );
                }
            );
    };

    const loadEditor = async () => {

        setIsLoading(true);
        setLoadingMessage("Loading Coaching Plan");

        Promise
            .all([
                getProtocols(),
                getActivityStats(),
                getCoachingPlanTrackData()
            ])
            .then(
                ([acts, aStats, _voidCoachingPlanTrackData]) => {
                    getCurrentPlan(acts, aStats);
                }
            )
            .catch(
                (error) => {
                    notificationManager.addError(error);
                    setIsLoading(false);
                }
            );
    };

    const handleSaveChanges = () => {
        if (!havePlan) {
            if (startDate !== null && selectedCoachingPlanTrack !== null && timeZone !== null) {
                InitializeFullPlan();
            }
            else {
                notificationManager.addError("Missing value to create Coaching Plan!");
            }
        }
        else if (editPlanSettings) {
            if (selectedCoachingPlanTrack !== null && timeZone !== null) {
                InitializeFullPlan();
            }
            else {
                notificationManager.addError("Missing value to update Coaching Plan settings!");
            }
        }
        else {
            let errorMessage = validatePlan();
            if (errorMessage !== null && errorMessage !== "") {
                notificationManager.addError(errorMessage.trim());
            }
            else {
                if (hasPlanChanged()) {
                    void savePlan();
                }
            }
        }
    };

    const getStateColor = (state) => {
        switch (state) {
            case "Active":
                return 0;
            case "Pending":
                return 1;
            case "Completed":
                return 2;
            case "Interrupted":
                return 3;
            case "Cancelled":
                return 3;
            default:
                return 0;
        }
    };

    const configureWeekOptions = (plans) => {
        let options = [];
        plans.forEach(
            (e, i) => {
                options.push(
                    {
                        label: `Week ${i}`,
                        value: i,
                        pill: {
                            value: (e.state === "Active" ? "Current" : e.state),
                            color: getStateColor(e.state)
                        }
                    }
                );

            }
        );
        setWeekOptions(options);
    };

    const setWeek = async(plans) => {
        await API.api_Get(
            componentName,
            `/patients/${patientId}`,
            notificationManager,
            (response) => {
                if (response.data.coaching_plan !== undefined) {
                    let patientWeekNum = response.data.coaching_plan.plan_week;

                    // Find the plan with matching plan_week number (not array index!)
                    let planIndex = plans.findIndex(p => p.plan_week === patientWeekNum);

                    if (planIndex === -1) {
                        console.warn(`${componentName}[setWeek]: No plan found for week ${patientWeekNum}. Available weeks: ${plans.map(p => p.plan_week).join(", ")}`);
                        notificationManager.addError(`Unable to load coaching plan: No plan exists for week ${patientWeekNum}.`);
                        setCurrentWeek(-1);
                        setSelectedWeek(-1);
                        return;
                    }

                    const plan = plans[planIndex];

                    if (plan.track_name !== "Default Week Zero") {
                        setCurrentWeek(planIndex);  // Store array index, not week number
                        setPlanName(plan.track_name.replaceAll(" - ", "_").replaceAll(" ", "_"));
                        setSelectedCoachingPlanTrack(plan.track_name.replaceAll(" - ", "_").replaceAll(" ", "_"));
                        setSelectedWeek(planIndex);  // Store array index
                        setTimeZone(plan.time_zone);
                    }
                    else {
                        // Default Week Zero case
                        setCurrentWeek(planIndex);  // Store array index, not week number
                        setPlanName(plan.track_name.replaceAll(" - ", "_").replaceAll(" ", "_"));
                        setSelectedCoachingPlanTrack("Upper_Extremity");
                        setSelectedWeek(planIndex);  // Store array index
                        setTimeZone(timeZoneOptions[0].value);
                    }
                }
                else {
                    if (plans[0] && plans[0].state !== "Completed" && plans[0].state !== "Cancelled" && plans[0].state !== "Interrupted") {
                        setCurrentWeek(-1);
                        setPlanName(plans[0].track_name.replaceAll(" - ", "_").replaceAll(" ", "_"));
                        setSelectedCoachingPlanTrack(plans[0].track_name.replaceAll(" - ", "_").replaceAll(" ", "_"));
                        setSelectedWeek(-1);
                        setTimeZone(plans[0].time_zone);
                    }
                    else if (plans.length > 0) {
                        let week = plans.length;
                        for (let i = 0; i < plans.length; i++) {
                            if (plans[i].state !== "Completed") {
                                week = i;
                                break;
                            }
                        }
                        setCurrentWeek(week);
                        setPlanName(plans[plans.length - 1].track_name.replaceAll(" - ", "_").replaceAll(" ", "_"));
                        setSelectedCoachingPlanTrack(plans[plans.length - 1].track_name.replaceAll(" - ", "_").replaceAll(" ", "_"));
                        setSelectedWeek(week);
                        setTimeZone(plans[plans.length - 1].time_zone);
                    }
                }
            },
            (error) => {
                notificationManager.addError(error);
            }
        );
    };

    const setUIFields = () => {
        setInit(false);
        if (coachingPlans?.length > 0) {
            if (coachingPlans[0].track_name !== "Default Week Zero") {
                setStateView(renderState());
            }
            else {
                if (coachingPlanTrackOptions?.length > 0) {
                    setSelectedCoachingPlanTrack(coachingPlanTrackOptions[0].value);
                    setTimeZone(timeZoneOptions[0].value);
                }
                else {
                    setSelectedCoachingPlanTrack("Upper_Extremity");
                    setTimeZone(timeZoneOptions[0].value);
                }
            }
        }
        else {
            if (coachingPlanTrackOptions?.length > 0) {
                setSelectedCoachingPlanTrack(coachingPlanTrackOptions[0].value);
                setTimeZone(timeZoneOptions[0].value);
            }
        }
    };

    const formatWeek = (week, proto, act) => {
        let newWeek = [];

        if (proto && act) {
            week.forEach(
                element => {
                    if (element && !element.Settings && activityStats) {
                        setValueAtPath(element, "Settings.Duration", getValueAtPath(activityStats[element.Id], "Settings.Duration.Value", 120));
                    }

                    newWeek.push(element);
                }
            );

            while (newWeek.length < 4) {
                newWeek.push(null);
            }
        }

        return newWeek;
    };

    const formatPlans = (plans, acts) => {
        let formattedPlans = [];
        let actList = acts.map(proto => proto.id);
        plans.forEach(
            (plan) => {
                plan.assignments = formatWeek(plan.assignments, [], actList);
                formattedPlans.push(plan);
            }
        );
        return formattedPlans;
    };

    const forceSetupPage = () => {
        setCoachingPlans([]);
        setPlan(false);
        setEditPlanSettings(false);
        setSelectedCoachingPlanTrack(coachingPlanTrackOptions[0].value);
        setTimeZone(timeZoneOptions[0].value);
        setCurrentWeek(-1);
        setSelectedWeek(-1);
    };

    const InitializeFullPlan = () => {
        if (!editPlanSettings) {
            setCoachingPlans(null);
        }
        else {
            // Defensive check: if we're in edit mode but have no plans, something went wrong
            if (!coachingPlans || coachingPlans.length === 0) {
                console.error("InitializeFullPlan: editPlanSettings is true but coachingPlans is empty. Resetting to new plan mode.");
                setEditPlanSettings(false);
                setCoachingPlans(null);
                setPlan(true);
                setInit(true);
                return;
            }

            let plans = trackPlan[selectedCoachingPlanTrack].plans;
            let temp = coachingPlans;

            if (temp[0].track_name !== "Default Week Zero") {
                if (coachingPlans[currentWeek !== -1 ? currentWeek : 0].track_name !== trackPlan[selectedCoachingPlanTrack].track_name) {
                    for (let i = 0; i < plans.length; i++) {
                        temp[i].assignments = plans[i].assignments;
                    }
                    setCoachingPlans(temp);
                    setPlan(true);
                    setInit(true);
                    return;
                }
                else {
                    if (timeZone !== coachingPlans[currentWeek !== -1 ? currentWeek : 0].time_zone) {
                        setPlan(true);
                        setInit(true);
                        return;
                    }
                    else {
                        setInit(false);
                        return;
                    }
                }
            }
            else {
                let plans = trackPlan[selectedCoachingPlanTrack].plans;
                let temp = coachingPlans;

                for (let i = 1; i < (temp.length > 13 ? temp.length : 13); i++) {
                    let obj = Object.create(temp[0]);
                    temp.push(obj);
                    temp[i].assignments = plans[i].assignments;
                }

                setCoachingPlans(temp);
                setPlan(true);
                setInit(true);
                return;
            }
        }

        setPlan(true);
        setInit(true);
    };

    const goToCurrentWeek = () => {
        changeWeek(currentWeek);
    };

    // ---=== useCallbacks ===--- //

    const setBonusActivityCallback = useCallback(
        (value, i, j) => {
            if (value) {
                if (coachingPlans[i].assignments[j]) {
                    if (coachingPlans[i].assignments[j].Id === value) {
                        return;
                    }
                }

                coachingPlans[i].assignments[j] = {
                    "Id": value,
                    "Settings": {
                        "Duration": value
                    }
                };
            }
            else {
                coachingPlans[i].assignments[j] = null;
            }

            setCoachingPlans(coachingPlans);
        }, [coachingPlans]
    );

    const setBonusDurationCallback = useCallback(
        (value, id, i, j) => {
            if (value && !isNaN(value)) {
                let settings = coachingPlans[i].assignments[j].Settings;

                if (!settings) {
                    settings = {
                        "Duration": value
                    };
                }
                else {
                    if (settings.Duration !== undefined) {
                        settings.Duration = value;
                    }
                    else {
                        settings["Duration"] = value;
                    }
                }

                coachingPlans[i].assignments[j] = {
                    "Id": id,
                    "Settings": settings
                };

                setCoachingPlans(coachingPlans);
            }
        },[coachingPlans]
    );

    const setBonusOptionsCallback = useCallback(
        (label, value, i, j) => {
            if (coachingPlans[i].assignments[j].Settings === undefined) {
                coachingPlans[i].assignments[j].Settings = {};
            }
            coachingPlans[i].assignments[j].Settings[label] = value;
            setCoachingPlans(coachingPlans);
        }, [coachingPlans]
    );

    const setPersonalRoutineActivityCallback = useCallback(
        (value, i, j) => {
            if (value) {
                if (personalRoutine) {
                    if (personalRoutine.assignments[j].Id === value) {
                        return;
                    }
                    personalRoutine.assignments[j] = { "Id": value, "Settings": { "Duration": getValueAtPath(settings[value], "Settings.Duration.Value", 120) } };
                }
                setPersonalRoutine(personalRoutine);
            }
        }, [personalRoutine]
    );

    const setPersonalRoutineDurationCallback = useCallback(
        (value, id, i, j) => {
            if (value && !isNaN(value)) {
                personalRoutine.assignments[j] ? personalRoutine.assignments[j]["Settings"]["Duration"] = value : personalRoutine.assignments[j] = { "Id": id, "Settings": { "Duration": value } };
                setPersonalRoutine(personalRoutine);
            }
        }, [personalRoutine]
    );

    const setPersonalRoutineOptionsCallback = useCallback(
        (label, value, i, j) => {
            if (personalRoutine.assignments[j] !== undefined && personalRoutine.assignments[j].Settings !== undefined && personalRoutine.assignments[j].Settings[label] !== undefined) {
                personalRoutine.assignments[j].Settings[label] = value;
            }
            else {
                if (personalRoutine.assignments[j].Settings === undefined) {
                    personalRoutine.assignments[j].Settings = {};
                }
                personalRoutine.assignments[j].Settings[label] = value;
            }
            setPersonalRoutine(personalRoutine);
        }, [personalRoutine]
    );

    const getDuplicateActivityIds = (assignments, exclusions = null) => {
        const idCounts = new Map();
        const duplicateIds = new Set();

        assignments.forEach(
            (assignment) => {
                if (assignment?.Id) {
                    if (!exclusions?.includes(assignment.Id)) {
                        const count = idCounts.get(assignment.Id) || 0;
                        idCounts.set(assignment.Id, count + 1);
                        if (count + 1 > 1) {
                            duplicateIds.add(assignment.Id);
                        }
                    }
                }
            }
        );

        return duplicateIds;
    };

    const validatePersonalRoutineAssignmentsUnique = (assignments) => {
        let duplicates = getDuplicateActivityIds(assignments, ["Breathing"]);
        return duplicates.size > 0 ? `Duplicate Personal Routine activities:\n[${Array.from(duplicates).join(", ")}]\n` : "";
    };

    const validateBonusAssignmentsUnique = (assignments, week) => {
        let duplicates = getDuplicateActivityIds(assignments);
        return duplicates.size > 0 ? `Duplicate Bonus activities in week ${week}\n[${Array.from(duplicates).join(", ")}]\n` : "";
    };

    const validateBonusAssigmentDurations = (arry, week) => {
        let ids = [];
        arry.forEach(
            element => {
                if (element && element.Duration !== undefined && isNaN(element.Duration)) {
                    ids.push(element.Id);
                }
            }
        );
        return ids.length > 0 ? `Duration not set or not a number for Bonus activities in week ${week}\n[${ids.join(", ")}]\n` : "";
    };

    const validatePlan = (errorMessage = null) => {
        errorMessage = errorMessage || "";
        errorMessage += validatePersonalRoutineAssignmentsUnique(personalRoutine.assignments);
        coachingPlans.forEach(
            (plan, i: number) => {
                errorMessage += validateBonusAssignmentsUnique(plan.assignments, i);
                errorMessage += validateBonusAssigmentDurations(plan.assignments, i);
            }
        );
        return errorMessage;
    };

    const haveSettingsChanged = () => {
        return planName !== selectedCoachingPlanTrack || timeZone !== coachingPlans[currentWeek !== -1 ? currentWeek : 0].time_zone;
    };

    const haveAssignmentsChanged = () => {
        if (coachingPlans === null || duplicatePlans === null) {return false;}
        if (personalRoutine === null || duplicateProtocol === null) {return false;}
        if (coachingPlans.length === 0 && duplicatePlans.length === 0) {return false;}

        for (let index = 0; index < coachingPlans.length; index++) {
            const plan = coachingPlans[index];
            const cleanedAssignments = JSON.stringify(cleanAssignments(plan.assignments));
            if (cleanedAssignments !== duplicatePlans[index].assignments) {
                return true;
            }
        }

        if (personalRoutine.enabled !== personalRoutineEnabled) {
            return true;
        }

        let cleanedProtocolAssignments = JSON.stringify(cleanAssignments(personalRoutine.assignments));
        return cleanedProtocolAssignments !== duplicateProtocol;
    };

    const hasPlanChanged = () => {
        return haveSettingsChanged() || haveAssignmentsChanged();
    };

    let actFilter = [];

    const timeZoneOptions = [
        { label: "Pacific (PST)", value: "US/Pacific" },
        { label: "Arizona (AST)", value: "US/Arizona" },
        { label: "Mountain (MST)", value: "US/Mountain" },
        { label: "Central (CST)", value: "US/Central" },
        { label: "Eastern (EST)", value: "US/Eastern" }
    ];

    const cleanAssignments = (assignments, aStats = activityStats) => {
        let cleanedAssignments = assignments.filter(assignment => assignment);
        cleanedAssignments.forEach(
            assignment => {
                if (assignment.Id) {
                    delValueAtPath(assignment, "MinVersion");

                    if (getValueAtPath(aStats[assignment.Id], "Settings.DurationIncrement.Value") === -1) {
                        delValueAtPath(assignment, "Settings.Duration");
                    }

                    delValueAtPath(assignment, "Settings.DurationIncrement");

                    if ("Settings" in assignment && Object.keys(assignment.Settings).length === 0) {
                        delValueAtPath(assignment, "Settings");
                    }

                    // These are placeholder key deletions due to the data migrations
                    // on PROD not properly reformatting all the PersonalAssignment
                    // and CoachingPlan records. They can be removed after the
                    // migrations have been applied correctly.
                    if ("Duration" in assignment) {
                        delValueAtPath(assignment, "Duration");
                    }
                    if ("DurationIncrement" in assignment) {
                        delValueAtPath(assignment, "DurationIncrement");
                    }
                }
            }
        );
        return cleanedAssignments;
    };

    const convertOldAssignmentDurations = (assignments) => {
        let convertedAssignments = assignments.filter(assignment => assignment);
        convertedAssignments.forEach(
            assignment => {
                if (assignment.Id) {
                    if ("Duration" in assignment) {
                        setValueAtPath(assignment, "Settings.Duration", assignment.Duration);
                        delValueAtPath(assignment, "Duration");
                    }
                    if ("DurationIncrement" in assignment) {
                        delValueAtPath(assignment, "DurationIncrement");
                    }
                }
            }
        );
        return convertedAssignments;
    };

    const togglePersonalRoutine = () => {
        if (personalRoutine) {
            personalRoutine.enabled = !personalRoutine.enabled;
            setPersonalRoutine(personalRoutine);
            setUIFields();
        }
    };

    //////////////////////
    // window overloads //
    //////////////////////

    window.onbeforeunload = confirmExit;

    function confirmExit (event) {
        if (!isLoading && hasPlanChanged()) {
            const confirmationMessage = "\nWARNING!\n\nThere are unsaved changes to the Coaching Plan!\n\nAre you sure you want to leave this page?\n";
            event.preventDefault();
            event.returnValue = confirmationMessage;
            return confirmationMessage;
        }

        return undefined;
    }

    const submitToUpdatePlanSettings = (template) => {
        if (template === undefined) {
            return;
        }

        if (template === selectedCoachingPlanTrack) {
            return;
        }

        notificationManager.clearAllNotifications();

        let text = "WARNING!\n\nAre you sure you want to change the current Coaching Plan Track?\n\nAny custom changes made to activity assignments in the current or future weeks will be replaced.";
        if (!window.confirm(text)) {
            return;
        }

        setSelectedCoachingPlanTrack(template);
        setEditPlanSettings(true);
        let plans = trackPlan[template].plans;
        let temp = coachingPlans;
        if (temp[0].track_name !== "Default Week Zero") {
            if (coachingPlans[currentWeek !== -1 ? currentWeek : 0].track_name !== trackPlan[template].track_name) {
                for (let i = 0; i < plans.length; i++) {
                    temp[i].assignments = formatPlanAssignments(plans[i].assignments);
                }
                setCoachingPlans(temp);
                setPlan(true);
                setInit(true);
                return;
            }
            else {
                if (timeZone !== coachingPlans[currentWeek !== -1 ? currentWeek : 0].time_zone) {
                    setPlan(true);
                    setInit(true);
                    return;
                }
                else {
                    setInit(false);
                    return;
                }
            }
        }
        else {
            let plans = trackPlan[template].plans;
            let temp = coachingPlans;
            for (let i = 1; i < (temp.length > 13 ? temp.length : 13); i++) {
                let obj = Object.create(temp[0]);
                temp.push(obj);
                temp[i].assignments = formatPlanAssignments(plans[i].assignments);

            }
            setCoachingPlans(temp);
            setPlan(true);
            setInit(true);
        }
    };

    const changeWeek = (week) => {
        if (selectedWeek === null || selectedWeek === week) {
            return;
        }

        if (haveAssignmentsChanged()) {
            let text = "\nWARNING!\n\nThere are unsaved changes to the Coaching Plan!\n\nChanging the week will result in the loss of these changes.\n\nAre you sure you want to change the week?\n";
            if (window.confirm(text)) {
                personalRoutine.assignments = JSON.parse(revertedRoutine);
                coachingPlans[selectedWeek].assignments = JSON.parse(revertedPlans)[selectedWeek].assignments;
                setPersonalRoutine(personalRoutine);
                setCoachingPlans(coachingPlans);
                setSelectedWeek(week);
            }
            else {
                setStateView(renderState());
            }
        }
        else {
            setSelectedWeek(week);
        }
    };

    const addWeeksToPlan = async (weeks: number) => {
        if (!confirmAddWeeksToPlan()) {
            return
        }

        let text = `\nWARNING!\n\nAre you sure you want to add ${weeks} week${weeks === 1 ? "" : "s"} to the Coaching Plan?\n\nExtra weeks can only be removed by a Karuna Admin.`;

        if (window.confirm(text) === true) {
            setEditPlanSettings(true);

            setIsLoading(true);
            setLoadingMessage(`Adding ${weeks} Week${weeks === 1 ? "" : "s"} to the Coaching Plan`);

            let result: ApiCallResult = createDefaultApiCallResult({ wasSuccessful: true });

            result = await API.api_Post(
                componentName,
                `/coaching_plan_track_instances/${instanceID}/add_weeks/?num_weeks=${weeks}`,
                null,
                notificationManager,
                (_response) => {
                },
                (error) => {
                    notificationManager.addError(error);
                }
            );

            if (result.wasSuccessful) {
                notificationManager.addSuccess(`Added ${weeks} Week${weeks === 1 ? "" : "s"} to the Coaching Plan`, { duration: 10 });
                void loadEditor();
            }
            else {
                notificationManager.addError(`Failed to add ${weeks} Week${weeks === 1 ? "" : "s"} to the Coaching Plan.`);
                setIsLoading(false);
            }
        }
    };

    const confirmAddWeeksToPlan = () => {
        notificationManager.clearAllNotifications();

        if (hasPlanChanged()) {
            notificationManager.addError("Unsaved changes to Coaching Plan! Save or revert any changes before adding additional weeks.");
            return false;
        }
        return true;
    };

    // ---=== Render ===--- //

    useEffect(
        () => {
            void loadEditor();
        },
        []
    );

    useEffect(() => {
        if (init) {
            if (!editPlanSettings) {
                void createNewPlan();
            }
            else {
                void updatePlanSettings();
            }
        }
    }, [init]);

    useEffect(
        () => {
            if (!isLoading && selectedWeek !== null && currentWeek !== null) {
                setUIFields();
                setStateView(renderState());
            }
        },
        [
            isLoading,
            selectedWeek,
            currentWeek,
            settings
        ]
    );

    const renderState = () => {
        if (isLoading || selectedWeek === null) {
            return;
        }

        if (coachingPlans?.length === 0 && activityStats && currentWeek === -1) {
            return (
                <NewPlan
                    coachingPlanTrackOptions={coachingPlanTrackOptions}
                    name={patientName}
                    selectedCoachingPlanTrack={selectedCoachingPlanTrack}
                    timeZone={timeZone}
                    timeZoneOptions={timeZoneOptions}
                    // Callbacks
                    onSetCoachingPlanTrack={setSelectedCoachingPlanTrack}
                    onSetTimeZone={setTimeZone}
                    onStartNewPlan={handleSaveChanges}
                />
            );
        }
        else if (coachingPlans?.length && activityStats) {
            if (coachingPlans[0].track_name === "Default Week Zero") {
                return (
                    <DefaultZeroView
                        coachingPlanTrackOptions={coachingPlanTrackOptions}
                        name={patientName}
                        selectedCoachingPlanTrack={selectedCoachingPlanTrack}
                        timeZone={timeZone}
                        timeZoneOptions={timeZoneOptions}
                        // Callbacks
                        onSetEdit={setEditPlanSettings}
                        onSetTemplatePlan={setSelectedCoachingPlanTrack}
                        onSetTimeZone={setTimeZone}
                        onStartNewPlan={handleSaveChanges}
                    />
                );
            }
            else {
                if (trackInstance.state === "Active") {
                    if (currentWeek === selectedWeek) {
                        return (
                            <CurrentWeekView
                                activityStats={activityStats}
                                coachingPlan={coachingPlans}
                                constPlan={constPlan}
                                constRoutine={constRoutine}
                                name={patientName}
                                personalRoutine={personalRoutine}
                                restrictCoachingPlanEnd={restrictCoachingPlanEnd}
                                routineScoreData={bestPriority}
                                selectedCoachingPlanTrack={selectedCoachingPlanTrack}
                                selectedWeek={selectedWeek}
                                coachingPlanTrackOptions={coachingPlanTrackOptions}
                                vetKitVersion={vetKitVersion}
                                weekOptions={weekOptions}
                                // Callbacks
                                onAddWeeks={addWeeksToPlan}
                                onChangeWeek={changeWeek}
                                onCompleteWeek={startWeek}
                                onConfirmAddWeeks={confirmAddWeeksToPlan}
                                onEndPlan={endPlan}
                                onSaveChanges={handleSaveChanges}
                                onSetTemplate={submitToUpdatePlanSettings}
                                onTogglePersonalRoutine={togglePersonalRoutine}
                                setBonusActivityCallback={setBonusActivityCallback}
                                setPersonalRoutineActivityCallback={setPersonalRoutineActivityCallback}
                                setBonusDurationCallback={setBonusDurationCallback}
                                setPersonalRoutineDurationCallback={setPersonalRoutineDurationCallback}
                                setBonusOptionsCallback={setBonusOptionsCallback}
                                setPersonalRoutineOptionsCallback={setPersonalRoutineOptionsCallback}
                            />
                        );
                    }
                    else if (currentWeek < selectedWeek) {
                        return (
                            <FutureWeekView
                                activityStats={activityStats}
                                coachingPlan={coachingPlans}
                                constPlan={constPlan}
                                name={patientName}
                                selectedWeek={selectedWeek}
                                vetKitVersion={vetKitVersion}
                                weekOptions={weekOptions}
                                // Callbacks
                                goToCurrentWeek={goToCurrentWeek}
                                onAddWeeks={addWeeksToPlan}
                                onChangeWeek={changeWeek}
                                onConfirmAddWeeks={confirmAddWeeksToPlan}
                                onSaveChanges={handleSaveChanges}
                                setBonusActivityCallback={setBonusActivityCallback}
                                setBonusDurationCallback={setBonusDurationCallback}
                                setBonusOptionsCallback={setBonusOptionsCallback}
                                setPersonalRoutineOptionsCallback={setPersonalRoutineOptionsCallback}
                            />
                        );
                    }
                    else {
                        return (
                            <PastWeekView
                                activityStats={activityStats}
                                coachingPlan={coachingPlans}
                                constPlan={constPlan}
                                constRoutine={constRoutine}
                                name={patientName}
                                personalRoutine={personalRoutine}
                                routineScoreData={bestPriority}
                                selectedWeek={selectedWeek}
                                weekOptions={weekOptions}
                                // Callbacks
                                goToCurrentWeek={goToCurrentWeek}
                                onChangeWeek={changeWeek}
                            />
                        );
                    }
                }
                else {
                    if (currentWeek === selectedWeek) {
                        return (
                            <CompletedCurrentWeekView
                                constPlan={constPlan}
                                activityStats={activityStats}
                                coachingPlan={coachingPlans}
                                constRoutine={constRoutine}
                                name={patientName}
                                personalRoutine={personalRoutine}
                                routineScoreData={bestPriority}
                                selectedWeek={selectedWeek}
                                weekOptions={weekOptions}
                                // Callbacks
                                onChangeWeek={changeWeek}
                                onStartNewPlan={forceSetupPage}
                            />
                        );
                    }
                    else if (currentWeek < selectedWeek) {
                        return (
                            <CompletedFutureWeekView
                                activityStats={activityStats}
                                coachingPlan={coachingPlans}
                                constPlan={constPlan}
                                constRoutine={constRoutine}
                                name={patientName}
                                selectedWeek={selectedWeek}
                                weekOptions={weekOptions}
                                // Callbacks
                                onChangeWeek={changeWeek}
                                onStartNewPlan={forceSetupPage}
                            />
                        );
                    }
                    else {
                        return (
                            <CompletedPastWeekView
                                activityStats={activityStats}
                                coachingPlan={coachingPlans}
                                constPlan={constPlan}
                                constRoutine={constRoutine}
                                name={patientName}
                                personalRoutine={personalRoutine}
                                routineScoreData={bestPriority}
                                selectedWeek={selectedWeek}
                                weekOptions={weekOptions}
                                // Callbacks
                                onChangeWeek={changeWeek}
                                onStartNewPlan={forceSetupPage}
                            />
                        );
                    }
                }
            }
        }
    };

    const onPatientData = (patientData) => {
        if (patientData) {
            setPatientName(`${patientData.first_name ? patientData.first_name : ""} ${patientData.last_name ? patientData.last_name : ""}`);

            if (patientData.client?.version) {
                let version = patientData.client.version.split("+unitycloudbuild")[0];
                if (version.includes("prod")) {
                    version = version.split("-")[0];
                }
                setVetKitVersion(version);
            }

            if (patientData.profile?.restrict_coaching_plan_end) {
                setRestrictCoachingPlanEnd(true);
            }
        }
    };

    return (
        <PatientProfile
            parentComponentName={componentName}
            // Callbacks
            onPatientData={onPatientData}
        >
            {
                isLoading
                    ? (
                        renderLoader(loadingMessage)
                    )
                    : stateView && (
                        <div className="w-full h-full">{stateView}</div>
                    )
            }
        </PatientProfile>
    );
};

export default EditCoachingPlan;
